<template>
    <head>
        <link
            href="https://fonts.googleapis.com/css?family=Space Grotesk"
            rel="stylesheet"
        />
    </head>
    <div class="analytics-container">
        <!-- Guest Banner (shown to unauthenticated users) -->
        <GuestBanner v-if="isGuest" message="Sign up to track your learning progress" />

        <!-- Main Content -->
        <div class="main-content">
            <!-- Page Header (authenticated users only) -->
            <AnalyticsHeader v-if="!isGuest" />
            <!-- Loading State -->
            <div v-if="loading" class="loading">
                <div class="spinner"></div>
                <p>Loading analytics...</p>
            </div>

            <!-- Analytics Display -->
            <div v-else-if="analytics" class="analytics-section">
                <!-- Overall Stats -->
                <AnalyticsStats :analytics="analytics" />

                <!-- Topic Performance -->
                <TopicPerformance
                    v-if="Object.keys(analytics.topics_performance).length > 0"
                    :topics-performance="analytics.topics_performance"
                    :view-mode="viewMode"
                    :is-premium="hasAdvancedAccess"
                    :filtered-topics="filteredTopics"
                    :selected-module="selectedModule"
                    :sort-by="sortBy"
                    :sort-order="sortOrder"
                    :search-query="searchQuery"
                    @toggle-view="toggleViewMode"
                    @update:selectedModule="updateSelectedModule"
                    @update:sortBy="updateSortBy"
                    @update:sortOrder="updateSortOrder"
                    @update:searchQuery="updateSearchQuery"
                    @sort="setSortBy"
                />

                <!-- Difficulty Performance -->
                <DifficultyPerformance
                    v-if="
                        Object.keys(analytics.difficulty_performance).length > 0
                    "
                    :difficulty-performance="analytics.difficulty_performance"
                />

                <!-- Recent Attempts -->
                <RecentAttempts
                    v-if="analytics.recent_attempts.length > 0"
                    :recent-attempts="analytics.recent_attempts"
                />
            </div>

            <!-- No Data State -->
            <div v-else class="no-data">
                <h3>No analytics available</h3>
                <p>Complete some questions to see your analytics!</p>
            </div>
        </div>

        <!-- Premium Paywall Modal -->
        <PremiumPaywall
            :show="showPaywall"
            @close="showPaywall = false"
            @upgrade="handleUpgrade"
        />

        <!-- Guest Blocking Modal -->
        <GuestBlockingModal
            :show="isGuest"
            featureName="Analytics"
        />
    </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import api from "@/services/api";
import {
    getModuleForTopic,
    getTopicLabel,
    moduleMapping,
} from "@/utils/topicHelpers";

import AnalyticsStats from "@/components/analytics/AnalyticsStats.vue";
import TopicPerformance from "@/components/analytics/TopicPerformance.vue";
import DifficultyPerformance from "@/components/analytics/DifficultyPerformance.vue";
import RecentAttempts from "@/components/analytics/RecentAttempts.vue";
import PremiumPaywall from "@/components/analytics/PremiumPaywall.vue";
import AnalyticsHeader from "@/components/analytics/AnalyticsHeader.vue";
import GuestBlockingModal from "@/components/common/GuestBlockingModal.vue";
import GuestBanner from "@/components/common/GuestBanner.vue";

export default {
    name: "Analytics",
    components: {
        AnalyticsStats,
        TopicPerformance,
        DifficultyPerformance,
        RecentAttempts,
        PremiumPaywall,
        AnalyticsHeader,
        GuestBlockingModal,
        GuestBanner,
    },
    setup() {
        const router = useRouter();
        const authStore = useAuthStore();

        const loading = ref(true);
        const analytics = ref(null);
        const viewMode = ref("quick");
        const showPaywall = ref(false);

        const selectedModule = ref("all");
        const sortBy = ref("accuracy");
        const sortOrder = ref("asc");
        const searchQuery = ref("");

        const user = computed(() => authStore.user);
        const isGuest = computed(() => !authStore.isAuthenticated);
        const hasAdvancedAccess = computed(() => authStore.hasTierAccess("TIER 1"));

        const loadAnalytics = async (isAdvanced = false) => {
            loading.value = true;
            try {
                const token = localStorage.getItem("token");

                if (!user.value?.id) {
                    console.error("No user ID available");
                    router.push("/");
                    return;
                }

                const params = isAdvanced
                    ? {
                          limit: null,
                          min_attempts: 1,
                          sort_by: sortBy.value,
                          sort_order: sortOrder.value,
                      }
                    : {
                          limit: 5,
                          min_attempts: 3,
                          sort_by: "accuracy",
                          sort_order: "asc",
                      };

                const response = await api.getUserAnalytics(
                    user.value.id,
                    params,
                );
                analytics.value = response.data;
            } catch (error) {
                console.error("Error loading analytics:", error);
                if (error.response?.status === 401) {
                    alert("Authentication error. Please log in again.");
                    authStore.logout();
                    router.push("/");
                } else {
                    alert("Failed to load analytics. Please try again.");
                }
            } finally {
                loading.value = false;
            }
        };

        const loadAdvancedAnalytics = () => {
            loadAnalytics(true);
        };

        const toggleViewMode = () => {
            if (viewMode.value === "quick") {
                if (!authStore.hasTierAccess("TIER 1")) {
                    showPaywall.value = true;
                    return;
                }
                viewMode.value = "advanced";
                loadAdvancedAnalytics();
            } else {
                viewMode.value = "quick";
                loadAnalytics(false);
            }
        };

        const setSortBy = (field) => {
            if (sortBy.value === field) {
                sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
            } else {
                sortBy.value = field;
                sortOrder.value = "asc";
            }
            loadAdvancedAnalytics();
        };

        const updateSelectedModule = (value) => {
            selectedModule.value = value;
            loadAdvancedAnalytics();
        };

        const updateSortBy = (value) => {
            sortBy.value = value;
            loadAdvancedAnalytics();
        };

        const updateSortOrder = (value) => {
            sortOrder.value = value;
            loadAdvancedAnalytics();
        };

        const updateSearchQuery = (value) => {
            searchQuery.value = value;
        };

        const filteredTopics = computed(() => {
            if (!analytics.value?.topics_performance) return {};

            let topics = { ...analytics.value.topics_performance };

            if (selectedModule.value !== "all") {
                const moduleTopics = moduleMapping[selectedModule.value] || [];
                topics = Object.fromEntries(
                    Object.entries(topics).filter(([topic]) =>
                        moduleTopics.includes(topic),
                    ),
                );
            }

            if (searchQuery.value.trim()) {
                const query = searchQuery.value.toLowerCase();
                topics = Object.fromEntries(
                    Object.entries(topics).filter(
                        ([topic]) =>
                            getTopicLabel(topic)
                                .toLowerCase()
                                .includes(query) ||
                            topic.toLowerCase().includes(query),
                    ),
                );
            }

            return topics;
        });

        const handleUpgrade = () => {
            // Implement upgrade logic
        };

        onMounted(async () => {
            // Only load analytics for authenticated users
            if (authStore.isAuthenticated) {
                loadAnalytics(false);
                await authStore.fetchUser();
            } else {
                loading.value = false;
            }
        });

        return {
            loading,
            analytics,
            user,
            isGuest,
            viewMode,
            showPaywall,
            selectedModule,
            sortBy,
            sortOrder,
            searchQuery,
            filteredTopics,
            hasAdvancedAccess,
            toggleViewMode,
            loadAdvancedAnalytics,
            setSortBy,
            updateSelectedModule,
            updateSortBy,
            updateSortOrder,
            updateSearchQuery,
            handleUpgrade,
        };
    },
};
</script>

<style scoped>
* {
    font-family: "Space Grotesk";
    font-weight: 400;
    letter-spacing: 1px;
    scrollbar-color: var(--bg-primary);
}

::-webkit-scrollbar {
    width: 12px;
    height: 12px;
}

::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
}

::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.5);
}

.analytics-container {
    min-height: 100vh;
    background: var(--bg-primary);
    padding-bottom: 40px;
}

.main-content {
    max-width: 90%;
    margin: 0 auto;
    padding: 70px 10%;
}

.loading {
    text-align: center;
    padding: 60px 20px;
    color: var(--text-primary);
    opacity: 0.9;
}

.spinner {
    width: 50px;
    height: 50px;
    border: 4px solid rgba(255, 246, 227, 0.2);
    border-top: 4px solid var(--accent);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 20px;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.analytics-section {
    display: flex;
    flex-direction: column;
    gap: 30px;
}

.no-data {
    text-align: center;
    padding: 60px 20px;
    background: rgba(var(--bg-secondary-rgb), 0.6);
    border: 1px solid rgba(var(--accent-rgb), 0.4);
}

.no-data h3 {
    margin-bottom: 10px;
    color: var(--text-secondary);
}

.no-data p {
    color: var(--text-primary);
    opacity: 0.8;
}
</style>
